/**
 * Eventos Taller — migración web de ares9015.
 *
 * Lista las órdenes de taller por rango de fechas: abiertas y cerradas, con
 * sus gastos acumulados y lo pagado por el cliente. Permite ver el detalle
 * de gastos de cada orden y eliminar órdenes abiertas (la eliminación
 * devuelve el producto a su sucursal).
 */
import React, { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import CircularProgress from "@mui/material/CircularProgress";
import { DataGrid } from "@mui/x-data-grid";
import SearchIcon from "@mui/icons-material/Search";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import { useGlobalMDAlert } from "context";
import { FP, PageHeader, fpInputSx, fpDataGridSx } from "components/financepro";
import { useApi } from "lib/api";

const fmtMoney = (v) => {
  const n = typeof v === "number" ? v : parseFloat(String(v || "").replace(/,/g, ""));
  if (Number.isNaN(n)) return "0.00";
  return n.toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const hoyISO = () => new Date().toISOString().slice(0, 10);
const inicioMesISO = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
};

const btnBase = { textTransform: "none", borderRadius: 1.8, fontWeight: 700, boxShadow: "none" };
const btnSolid = (bg, hover) => ({
  ...btnBase,
  bgcolor: bg,
  color: "#FFFFFF",
  "&:hover": { bgcolor: hover, boxShadow: "none", color: "#FFFFFF" },
  "&.Mui-disabled": { bgcolor: `${bg}66`, color: "#FFFFFF" },
});
const btnOutline = (c) => ({
  ...btnBase,
  color: c,
  bgcolor: "#FFFFFF",
  border: `1px solid ${c}`,
  "&:hover": { bgcolor: `${c}12`, border: `1px solid ${c}`, color: c },
});
const btnGhost = {
  ...btnBase,
  color: FP.color.textSecondary,
  bgcolor: "transparent",
  "&:hover": { bgcolor: FP.color.surfaceAlt, color: FP.color.textPrimary },
};

const sectionBox = {
  border: `1px solid ${FP.color.border}`,
  borderRadius: 2.5,
  p: { xs: 2, sm: 2.6 },
  mb: 0,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
};

function NoRowsOverlay() {
  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      sx={{ height: "100%", py: 6, color: FP.color.textMuted }}
    >
      <InboxOutlinedIcon sx={{ fontSize: 44, mb: 1 }} />
      <Typography sx={{ fontWeight: 700, color: FP.color.textSecondary }}>
        Sin órdenes de taller en el rango
      </Typography>
    </Stack>
  );
}

export default function EventosTaller({ embedded = false, refreshToken = 0 } = {}) {
  const { showAlert } = useGlobalMDAlert();
  const { post } = useApi();

  const [fecha1, setFecha1] = useState(inicioMesISO());
  const [fecha2, setFecha2] = useState(hoyISO());
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const cargar = async () => {
    setLoading(true);
    try {
      const { ok, data } = await post("/api/taller_ordenes", {
        fechadesde: fecha1,
        fechahasta: fecha2,
      });
      if (ok) {
        setRows((data.ordenes || []).map((r) => ({ ...r, id: r.id })));
      } else {
        showAlert({ message: data.Error || "No se pudieron listar las órdenes", color: "error" });
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshToken]);

  // ---- Detalle de gastos ----
  const [detalle, setDetalle] = useState(null);
  const [cargandoDetalle, setCargandoDetalle] = useState(false);

  const verGastos = async (row) => {
    setCargandoDetalle(true);
    try {
      const { ok, data } = await post("/api/taller_orden_datos", { id: row.id });
      if (ok && data.orden) {
        setDetalle({ orden: data.orden, gastos: data.gastos || [] });
      } else {
        showAlert({
          message: data.Error || "No se pudo cargar el detalle de la orden",
          color: "error",
        });
      }
    } finally {
      setCargandoDetalle(false);
    }
  };

  // ---- Eliminación ----
  const [target, setTarget] = useState(null);
  const [eliminando, setEliminando] = useState(false);

  const eliminar = async () => {
    setEliminando(true);
    try {
      const { ok, data } = await post("/api/taller_orden_eliminar", { id: target.id });
      if (ok) {
        showAlert({ message: data.mensajeok || "Orden eliminada", color: "success" });
        setTarget(null);
        cargar();
      } else {
        showAlert({ message: data.Error || "No se pudo eliminar la orden", color: "error" });
      }
    } finally {
      setEliminando(false);
    }
  };

  const columns = [
    { field: "id", headerName: "Orden", width: 80 },
    { field: "fecha", headerName: "Entrada", width: 105 },
    { field: "chasis", headerName: "Chasis", width: 170 },
    {
      field: "vehiculo",
      headerName: "Vehículo",
      width: 150,
      valueGetter: (_v, row) => [row.marca, row.modelo].filter(Boolean).join(" "),
    },
    { field: "danofisico", headerName: "Daño físico", flex: 1, minWidth: 150 },
    { field: "responsable", headerName: "Responsable", width: 130 },
    {
      field: "status",
      headerName: "Estado",
      width: 140,
      renderCell: (params) =>
        params.value === "C" ? (
          <Chip
            size="small"
            label={`Cerrada${params.row.devueltoafabrica === "S" ? " (fábrica)" : ""}`}
            sx={{ bgcolor: "#E8F5E9", color: "#2E7D32", fontWeight: 700 }}
          />
        ) : (
          <Chip
            size="small"
            label="Abierta"
            sx={{ bgcolor: "#FFF3E0", color: "#E65100", fontWeight: 700 }}
          />
        ),
    },
    {
      field: "gasto",
      headerName: "Gastos",
      width: 110,
      align: "right",
      headerAlign: "right",
      valueFormatter: (value) => fmtMoney(value),
    },
    {
      field: "pagadocliente",
      headerName: "Pagado",
      width: 110,
      align: "right",
      headerAlign: "right",
      valueFormatter: (value) => fmtMoney(value),
    },
    { field: "fechasalida", headerName: "Salida", width: 105 },
    {
      field: "acciones",
      headerName: "",
      width: 220,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Stack direction="row" spacing={0.8}>
          <Button
            size="small"
            startIcon={<ReceiptLongOutlinedIcon />}
            onClick={() => verGastos(params.row)}
            disabled={cargandoDetalle}
            sx={btnOutline(FP.color.navy)}
          >
            Gastos
          </Button>
          {params.row.status !== "C" && (
            <Button
              size="small"
              startIcon={<DeleteOutlineIcon />}
              onClick={() => setTarget(params.row)}
              sx={btnOutline(FP.color.danger)}
            >
              Eliminar
            </Button>
          )}
        </Stack>
      ),
    },
  ];

  return (
    <Box component={embedded ? "section" : "main"} sx={embedded ? {} : { minHeight: "100vh", bgcolor: FP.color.surface }}>
      {!embedded && (
        <PageHeader
          title="Eventos Taller"
          subtitle="Consulta las órdenes de taller, su detalle de gastos y elimina órdenes abiertas"
        />
      )}
      <Box sx={{ p: embedded ? 0 : { xs: 2, md: 3 } }}>
        <Box sx={sectionBox}>
          <Grid container spacing={2} sx={{ mb: 1.5 }} alignItems="center">
            <Grid item xs={6} sm={3} md={2}>
              <TextField
                fullWidth
                type="date"
                label="Desde"
                InputLabelProps={{ shrink: true }}
                value={fecha1}
                onChange={(e) => setFecha1(e.target.value)}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <TextField
                fullWidth
                type="date"
                label="Hasta"
                InputLabelProps={{ shrink: true }}
                value={fecha2}
                onChange={(e) => setFecha2(e.target.value)}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12} sm={3} md={2}>
              <Button
                startIcon={
                  loading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />
                }
                onClick={cargar}
                disabled={loading}
                sx={btnSolid(FP.color.navy, FP.color.navySoft)}
              >
                Procesar
              </Button>
            </Grid>
            <Grid
              item
              xs={12}
              md={6}
              sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end" }}
            >
              <Typography
                sx={{ fontWeight: 700, color: FP.color.textSecondary, fontSize: "0.85rem" }}
              >
                {rows.length} órdenes
              </Typography>
            </Grid>
          </Grid>
          <Box sx={{ bgcolor: "#fff" }}>
            <DataGrid
              autoHeight
              rows={rows}
              columns={columns}
              loading={loading}
              rowHeight={46}
              pageSizeOptions={[25, 50]}
              initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
              slots={{ noRowsOverlay: NoRowsOverlay }}
              sx={{ ...fpDataGridSx, "--DataGrid-overlayHeight": "220px" }}
              disableRowSelectionOnClick
            />
          </Box>
        </Box>
      </Box>

      {/* Detalle de gastos de la orden */}
      <Dialog
        open={!!detalle}
        onClose={() => setDetalle(null)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 800, color: FP.color.navy }}>
          Gastos de la orden #{detalle?.orden?.id}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: "0.9rem", mb: 1.5 }}>
            Chasis <b>{detalle?.orden?.chasis}</b> ·{" "}
            {[detalle?.orden?.marca, detalle?.orden?.modelo].filter(Boolean).join(" ")}
          </DialogContentText>
          {detalle?.gastos?.length ? (
            <Box component="table" sx={{ width: "100%", borderCollapse: "collapse" }}>
              <Box component="thead">
                <Box component="tr" sx={{ bgcolor: FP.color.surfaceAlt }}>
                  {["Fecha", "Descripción", "Tipo", "Valor"].map((h) => (
                    <Box
                      component="th"
                      key={h}
                      sx={{
                        textAlign: h === "Valor" ? "right" : "left",
                        p: "6px 8px",
                        fontSize: "0.72rem",
                        color: FP.color.textSecondary,
                      }}
                    >
                      {h}
                    </Box>
                  ))}
                </Box>
              </Box>
              <Box component="tbody">
                {detalle.gastos.map((g, i) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <Box component="tr" key={i}>
                    <Box
                      component="td"
                      sx={{
                        p: "5px 8px",
                        fontSize: "0.8rem",
                        borderBottom: `1px solid ${FP.color.border}`,
                      }}
                    >
                      {g.fecha}
                    </Box>
                    <Box
                      component="td"
                      sx={{
                        p: "5px 8px",
                        fontSize: "0.8rem",
                        borderBottom: `1px solid ${FP.color.border}`,
                      }}
                    >
                      {g.descripcion}
                    </Box>
                    <Box
                      component="td"
                      sx={{
                        p: "5px 8px",
                        fontSize: "0.8rem",
                        borderBottom: `1px solid ${FP.color.border}`,
                      }}
                    >
                      {g.tipogastos}
                    </Box>
                    <Box
                      component="td"
                      sx={{
                        p: "5px 8px",
                        fontSize: "0.8rem",
                        textAlign: "right",
                        borderBottom: `1px solid ${FP.color.border}`,
                      }}
                    >
                      {fmtMoney(g.valor)}
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          ) : (
            <Typography sx={{ fontSize: "0.85rem", color: FP.color.textMuted }}>
              La orden no tiene gastos registrados.
            </Typography>
          )}
          <Stack direction="row" justifyContent="flex-end" sx={{ mt: 1.5 }}>
            <Typography sx={{ fontWeight: 800 }}>
              Total: RD$ {fmtMoney(detalle?.orden?.totalgastos)}
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setDetalle(null)} sx={btnGhost}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmación de eliminación */}
      <Dialog
        open={!!target}
        onClose={() => setTarget(null)}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 800, color: FP.color.danger }}>
          Eliminar orden de taller
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: "0.9rem" }}>
            Orden <b>#{target?.id}</b> del chasis <b>{target?.chasis}</b>. La orden se eliminará y
            el producto volverá a su sucursal. ¿Desea continuar?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setTarget(null)} sx={btnGhost}>
            No
          </Button>
          <Button
            startIcon={
              eliminando ? <CircularProgress size={16} color="inherit" /> : <DeleteOutlineIcon />
            }
            onClick={eliminar}
            disabled={eliminando}
            sx={btnSolid("#D32F2F", "#B71C1C")}
          >
            Sí, eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
